import { Select as AntSelect } from "antd";
import { Option, ReadonlyArray, Runtime } from "fp";

import React from "react";

import { ID } from "~/ID";
import * as Loading from "~/Loading";
import { Order } from "~/Order";
import { Remote } from "~/Remote";
import { Request } from "~/Request";
import { Search } from "~/Search";

export type Exemption = {
  readonly exemption: ID & {
    readonly name?: string;
    readonly abbreviation?: string;
  };
};

export function Exemption(exemption: Exemption) {
  return pipe(
    Exemption.use(exemption),
    Remote.map(Option.map((exemption) => <>{Exemption.toString(exemption)}</>)),
    Remote.getOrElseW(() => Option.none),
    Option.getOrElse(() => <Loading.Component />)
  );
}

export namespace Exemption {
  export const hasBreak = ({ exemption: { id } }: Exemption): boolean =>
    pipe(
      Exemptions.withBreaks().exemptions,
      ReadonlyArray.findFirst(({ exemption }) => id === exemption.id),
      Option.isSome
    );

  export const toString = ({
    exemption: { id, name, abbreviation },
  }: Exemption) => name ?? abbreviation ?? `Exemption ${id}`;

  export const toSearch: Search.From<Exemption> = ({
    exemption: { name, abbreviation },
  }) =>
    Search.join(
      name ? Search.string(name) : Search.empty(),
      abbreviation ? Search.string(abbreviation) : Search.empty()
    );

  export const use = ({
    exemption: { id },
  }: Exemption): Remote<Option<Exemption>> =>
    pipe(
      Exemptions.use(),
      Remote.map(
        flow(
          ({ exemptions }) => exemptions,
          ReadonlyArray.findFirst(({ exemption }) => id === exemption.id)
        )
      )
    );

  export namespace Response {
    export const runtime = () =>
      Runtime.strict({
        exemption_type_id: Runtime.number(),
        name: Runtime.nullable(Runtime.string()),
        abbreviation: Runtime.nullable(Runtime.string()),
      });

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Exemption => ({
      exemption: {
        ...ID.from(response.exemption_type_id),
        abbreviation: response.abbreviation ?? undefined,
        name: response.name ?? undefined,
      },
    });
  }

  export function Select({ selected, select }: Select.Props) {
    const exemptions = Exemptions.use();
    const loading = Remote.isLoading(exemptions);
    const options = pipe(
      exemptions,
      Remote.getOrElse(Exemptions.empty),
      Exemptions.Select.Options.fromType
    );

    return (
      <AntSelect
        allowClear={false}
        loading={loading}
        value={selected?.exemption.id}
        onChange={(id) => select({ exemption: { id } })}
        options={
          (options as never) as React.ComponentProps<
            typeof AntSelect
          >["options"]
        }
      />
    );
  }

  export namespace Select {
    export type Props = {
      readonly selected?: Exemption;
      readonly select: (exemption?: Exemption) => void;
    };

    export const use = () => {
      const [selected, setSelected] = React.useState<Exemption | undefined>();
      return { selected, select: setSelected };
    };
  }
}

export type Exemptions = { readonly exemptions: readonly Exemption[] };
export namespace Exemptions {
  export const empty = (): Exemptions => ({ exemptions: [] });

  export const withBreaks = (): Exemptions => ({
    exemptions: [
      { exemption: { id: "3" } }, // Oilfield
      { exemption: { id: "13" } }, // Federal Short Haul +16 Hour Exemption
    ] as const,
  });

  export const use = (): Remote<Exemptions> =>
    Remote.Query.use("exemption-types", request());

  export const request = (): Request.Fn<Exemptions> =>
    flow(
      Request.fromConfig({ endpoint: "/exemption-types" }),
      Request.Expect.status(200),
      Request.Expect.json(Response.runtime()),
      Request.onSuccess(Response.decode)
    );

  export namespace Response {
    export const runtime = () => Runtime.array(Exemption.Response.runtime());
    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Exemptions =>
      pipe(
        response,
        ReadonlyArray.map(Exemption.Response.decode),
        (exemptions) => ({ exemptions })
      );
  }

  export function Select({ selected, select, clear, ...props }: Select.Props) {
    const types = use();
    const loading = Remote.isLoading(types);
    const value = (selected?.exemptions ?? []).map(
      ({ exemption: { id } }) => id
    );

    const options = pipe(
      types,
      Remote.getOrElse(empty),
      Select.Options.fromType
    );

    const onChange = (ids: readonly string[]) =>
      pipe(
        options,
        ReadonlyArray.filterMap(({ exemption }) =>
          ids.includes(exemption.id) ? Option.some({ exemption }) : Option.none
        ),
        (exemptions) => select({ exemptions })
      );

    const filterOption = (value: string, option: Select.Option) =>
      Search.matches(Search.Match.string(value))(Exemption.toSearch(option));

    type AntSelectProps = React.ComponentProps<typeof AntSelect>;
    return (
      <AntSelect
        {...props}
        mode="multiple"
        loading={loading}
        value={value}
        onChange={onChange}
        onClear={clear}
        filterOption={filterOption as AntSelectProps["filterOption"]}
        options={
          (options as never) as React.ComponentProps<
            typeof AntSelect
          >["options"]
        }
      />
    );
  }

  export namespace Select {
    export type Props = Controls &
      Pick<
        React.ComponentProps<typeof AntSelect>,
        "bordered" | "placeholder" | "style"
      >;

    export type Controls = {
      readonly selected?: Partial<Exemptions>;
      readonly select: (types: Partial<Exemptions>) => void;
      readonly clear: () => void;
    };

    export namespace Controls {
      export const use = (): Controls => {
        const [selected, setSelected] = React.useState<Exemptions>(empty());
        return {
          selected,
          clear: () => setSelected(empty()),
          select: ({ exemptions = [] }) =>
            pipe(
              [...selected.exemptions, ...exemptions],
              ReadonlyArray.uniq({
                equals: (a, b) => a.exemption.id === b.exemption.id,
              }),
              (exemptions) => setSelected({ exemptions })
            ),
        };
      };
    }

    export type Option = Exemption & {
      readonly value: string;
      readonly label: string;
    };

    export type Options = readonly Option[];
    export namespace Options {
      export const fromType = ({ exemptions }: Exemptions): Options =>
        pipe(
          exemptions,
          ReadonlyArray.sort<Exemption>(
            Order.fromCompare((a, b) =>
              Order.string().compare(
                Exemption.toString(a),
                Exemption.toString(b)
              )
            )
          ),
          ReadonlyArray.map(({ exemption }) => ({
            exemption,
            value: exemption.id,
            label: Exemption.toString({ exemption }),
          }))
        );
    }
  }
}
